import heroPanel from '@cannect/assets/img/home/hero-panel.webp'
import { Link } from '@cannect/new-components/atoms'
import { JourneyCard } from '@cannect/new-components/molecules'
import { JOURNEY_CARDS } from '@cannect/new-components/molecules/JourneyCard/constants'
import type { CSSProperties } from 'react'
import { LuArrowRightCircle } from 'react-icons/lu'
import { buildConditionPath, HERO_BACKGROUND, HERO_CONDITIONS } from './constants'

const PROMO_PARAGRAPHS = [
  'Seu médico define o seu tratamento e nós te ajudamos a encontrar tudo o que você precisa. Qualquer tratamento, qualquer medicamento, na sua casa.',
  'E nós estamos sempre ao seu lado, acompanhando e orientando seu tratamento.',
  'Sua saúde em um só lugar.'
]

/**
 * Hero da home (Figma node 4333:300).
 *
 * O hero chegou aos 909px em cinco revisões: primeiro entraram a grade de doze condições e o card
 * promocional; depois a fileira de três pills virou os três `JourneyCard` com foto e subiu para
 * logo abaixo do subtítulo; então a grade e o texto promocional foram para dentro de um painel
 * único (node 4544:13814) com a foto de fundo, e o card promocional deixou de existir como card;
 * em seguida o painel passou na frente dos `JourneyCard`; agora **o título "O cuidado que você
 * sempre mereceu" saiu**. A ordem é painel → três cards: a explicação abre a seção e os três
 * caminhos fecham.
 *
 * **Com o título fora, quem é o H1 da página é o "Estamos aqui para te ajudar" dentro do painel.**
 * Ele carrega o `id="hero-heading"` que o `aria-labelledby` da section referencia. Era um `h2`;
 * virou `h1` para a home não ficar sem heading de primeiro nível e para o rótulo acessível da
 * seção não apontar para um id inexistente. O tamanho não mudou: as classes são explícitas.
 *
 * O painel tem margem de 20px de cada lado da viewport — não segue a coluna de conteúdo — e
 * arredonda **nos quatro cantos**: ele abre a seção mas não encosta no header (a section tem
 * `pt-10`, herdeiro do respiro que vinha do bloco do título) nem no fim, com os cards abaixo.
 * (Nas revisões anteriores era `rounded-t`, quando ele fechava a seção.) A largura interna
 * acompanha os 1166px do resto da página: a 1440 o design usa 119px de padding, e
 * `max-w-[1166px]` centralizado dá 117.
 *
 * Os cards mantêm 64px embaixo para não colar na `HowItWorksSection`, que vem logo depois com
 * fundo escuro.
 *
 * Cada condição tem paleta própria (ver `constants.ts`). O arquivo desenha os cards em
 * `neutral-50` com borda `neutral-100`, e o pastel entra **no hover** — decisão do time, para o
 * estado normal ficar sóbrio e a cor servir de afordância. Some com isso o problema de contraste
 * da revisão anterior, em que o tom forte como fundo reprovava AA em onze das doze condições:
 * sobre os pastéis o `neutral-800` rende 7,71–8,41:1.
 */
export const HeroSection = () => (
  <section aria-labelledby="hero-heading" className="w-full pt-10" style={{ backgroundImage: HERO_BACKGROUND }}>
    <div className="relative mx-5 overflow-hidden rounded-[16px]">
      <img src={heroPanel} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />

      <div className="relative mx-auto flex w-full max-w-[1198px] flex-col gap-16 px-4 py-[30px]">
        <div className="flex flex-col gap-[30px]">
          <h1
            id="hero-heading"
            className="m-0 max-w-[552px] text-[22px] font-normal leading-[1.2] tracking-[-0.736px] text-neutral-800">
            Estamos aqui para te ajudar <br className="hidden lg:inline" />a conquistar uma qualidade de vida melhor
          </h1>

          <div className="flex max-w-[578px] flex-col gap-5">
            {PROMO_PARAGRAPHS.map((paragraph) => (
              <p key={paragraph} className="m-0 text-sm leading-[1.4] text-neutral-800">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <p className="m-0 text-base leading-[1.6] text-neutral-800">Avalie sua necessidade agora</p>

          <ul className="m-0 flex max-w-[876px] list-none flex-wrap content-center items-center gap-2 p-0">
            {HERO_CONDITIONS.map((condition) => (
              <li key={condition.label} className="w-full sm:w-[284px]">
                <Link
                  to={buildConditionPath(condition)}
                  style={{ '--cond-hover': condition.colors.background } as CSSProperties}
                  className="flex h-full items-center justify-between gap-4 rounded-[8px] border border-solid border-neutral-100 bg-neutral-50 p-2 text-sm leading-normal text-neutral-800 no-underline transition-colors hover:bg-[var(--cond-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2">
                  <span className="flex min-w-0 flex-1 items-center gap-4">
                    <img
                      src={condition.icon}
                      alt=""
                      aria-hidden
                      loading="lazy"
                      decoding="async"
                      className="size-12 shrink-0 object-cover"
                    />
                    <span>{condition.label}</span>
                  </span>

                  <LuArrowRightCircle aria-hidden className="size-4 shrink-0 text-neutral-800" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* 30px entre o título e os parágrafos: no arquivo o título termina em y=397 e o primeiro
            parágrafo começa em 427. A revisão anterior reservava 84px de altura fixa para o título,
            o que deixava o painel 17px mais alto do que os 578 do arquivo. */}
        <div className="flex flex-col gap-[30px]">
          {/* `font-normal`: o arquivo usa Poppins Regular, e o reset do heading traria bold. O `<br>`
              só entra a partir de `lg` — o design quebra a linha depois de "ajudar". */}
          {/* <h1
            id="hero-heading"
            className="m-0 max-w-[552px] text-[22px] font-normal leading-[1.2] tracking-[-0.736px] text-neutral-800">
            Estamos aqui para te ajudar <br className="hidden lg:inline" />a conquistar uma qualidade vida melhor
          </h1> */}

          {/* <div className="flex max-w-[578px] flex-col gap-5">
            {PROMO_PARAGRAPHS.map((paragraph) => (
              <p key={paragraph} className="m-0 text-sm leading-[1.4] text-neutral-800">
                {paragraph}
              </p>
            ))}
          </div> */}
        </div>
      </div>
    </div>

    <div className="mx-auto w-full max-w-[1230px] px-8 pb-16 pt-10">
      <ul className="m-0 flex list-none flex-col gap-4 p-0 lg:flex-row">
        {JOURNEY_CARDS.map((card) => (
          <li key={card.title} className="flex min-w-0 lg:flex-1">
            <JourneyCard {...card} />
          </li>
        ))}
      </ul>
    </div>
  </section>
)
